import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, Input, message, InputNumber, Switch, Alert } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatGapWhInventory, useGetMatWarehouses } from '../../../hooks/genericHooks';
import type { VwMatGapWhInventory, MatWarehouses } from '../../../types';
import { insertData } from '../../../services/api';
import { useAuth } from '../../../components/common/security/AuthContext';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

type InventoryRow = VwMatGapWhInventory & { key: string };

export default function MatGapTxnTransfer() {
  const navigate = useNavigate();
  const { data: invData = [], isLoading, refetch: refetchInventory } = useGetMatGapWhInventory() as any;
  const { data: whData = [] } = useGetMatWarehouses() as any;

  const inventory: VwMatGapWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const { user: currentUser } = useAuth();

  // Owned warehouses by current user
  const ownedWhSet = useMemo(() => {
    const s = new Set<number>();
    (warehouses || []).forEach(w => {
      const owners: string[] = Array.isArray(w?.owners) ? (w.owners as string[]) : [];
      if (currentUser && owners.includes(String(currentUser))) s.add(Number(w.id));
    });
    return s;
  }, [warehouses, currentUser]);

  const baseWhOptions = useMemo(() => (warehouses || []).map(w => ({ value: w.id, label: w.name, disabled: !ownedWhSet.has(Number(w.id)) })), [warehouses, ownedWhSet]);
  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => m.set(w.id, w.name));
    return m;
  }, [warehouses]);

  // Controls
  const [fromWarehouseId, setFromWarehouseId] = useState<number | undefined>(undefined);
  const [toWarehouseId, setToWarehouseId] = useState<number | undefined>(undefined);
  const [date, setDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [search, setSearch] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [extGroupFilter, setExtGroupFilter] = useState<string>('');
  const [showZeroOnhand, setShowZeroOnhand] = useState<boolean>(true);

  const fromWhOptions = useMemo(() => (baseWhOptions || []).filter(o => o.value !== toWarehouseId), [baseWhOptions, toWarehouseId]);
  const toWhOptions = useMemo(() => (baseWhOptions || []).filter(o => o.value !== fromWarehouseId), [baseWhOptions, fromWarehouseId]);
  const hasAnyOwned = useMemo(() => ownedWhSet.size > 0, [ownedWhSet]);

  // Clear selections if they become invalid (not owned)
  React.useEffect(() => {
    if (fromWarehouseId != null && !ownedWhSet.has(Number(fromWarehouseId))) {
      setFromWarehouseId(undefined);
      setSelectedKeys([]);
      setQtyEdits({});
    }
    if (toWarehouseId != null && !ownedWhSet.has(Number(toWarehouseId))) {
      setToWarehouseId(undefined);
    }
  }, [ownedWhSet]);

  // Selection + editable quantities map (key => qty)
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [qtyEdits, setQtyEdits] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const isOwnerFrom = useMemo(() => {
    if (!fromWarehouseId || !currentUser) return false;
    const wh = (warehouses || []).find(w => Number(w.id) === Number(fromWarehouseId));
    const owners: string[] = Array.isArray(wh?.owners) ? (wh?.owners as string[]) : [];
    return owners.includes(String(currentUser));
  }, [fromWarehouseId, currentUser, warehouses]);
  const canTransact = Boolean(fromWarehouseId && currentUser && isOwnerFrom);

  const filtered: InventoryRow[] = useMemo(() => {
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const norm = (x: any) => (x == null ? '' : String(x)).trim().toLowerCase();

    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };

    return (inventory || [])
      .filter(r => (fromWarehouseId == null || r.warehouse_id === fromWarehouseId))
      .filter(r => (!groupFilter || norm(r.prod_group) === norm(groupFilter)))
      .filter(r => (!extGroupFilter || norm(r.ext_group) === norm(extGroupFilter)))
      .filter(r => (showZeroOnhand ? true : ((r.onhand_qty || 0) > 0)))
      .filter(matchesSearch)
      .map(r => ({ ...r, key: `${r.mat_id}-${r.warehouse_id}` }));
  }, [inventory, fromWarehouseId, search, groupFilter, extGroupFilter, showZeroOnhand]);

  const rowsByKey = useMemo(() => {
    const m: Record<string, InventoryRow> = {};
    filtered.forEach(r => { m[r.key] = r; });
    return m;
  }, [filtered]);

  const groupOptions = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim();
    const byKey = new Map<string, string>();
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };
    (inventory || [])
      .filter(r => fromWarehouseId == null || r.warehouse_id === fromWarehouseId)
      .filter(r => !extGroupFilter || norm(r.ext_group).toLowerCase() === norm(extGroupFilter).toLowerCase())
      .filter(matchesSearch)
      .forEach(r => {
        const raw = norm(r.prod_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [inventory, fromWarehouseId, extGroupFilter, search]);

  const extGroupOptions = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim();
    const byKey = new Map<string, string>();
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };
    (inventory || [])
      .filter(r => fromWarehouseId == null || r.warehouse_id === fromWarehouseId)
      .filter(r => !groupFilter || norm(r.prod_group).toLowerCase() === norm(groupFilter).toLowerCase())
      .filter(matchesSearch)
      .forEach(r => {
        const raw = norm(r.ext_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [inventory, fromWarehouseId, groupFilter, search]);

  // Auto-clear invalid selections when search or other filters remove available options
  React.useEffect(() => {
    if (groupFilter && !groupOptions.some(o => (o.value as string) === groupFilter)) {
      setGroupFilter('');
    }
  }, [groupOptions, groupFilter]);
  React.useEffect(() => {
    if (extGroupFilter && !extGroupOptions.some(o => (o.value as string) === extGroupFilter)) {
      setExtGroupFilter('');
    }
  }, [extGroupOptions, extGroupFilter]);

  const columns: ColumnsType<InventoryRow> = useMemo(() => [
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 130, sorter: (a, b) => String(a.mat_id || '').localeCompare(String(b.mat_id || '')) },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 90 },
    { title: 'Group', dataIndex: 'prod_group', key: 'prod_group', width: 160 },
    { title: 'Hierarchy (Long)', dataIndex: 'hierarchy_long', key: 'hierarchy_long', width: 220, ellipsis: true },
    { title: 'Ext Group', dataIndex: 'ext_group', key: 'ext_group', width: 140 },
    { title: 'From WH', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 160, render: (v: number) => whMap.get(v) || v },
    { title: 'On-hand', dataIndex: 'onhand_qty', key: 'onhand_qty', width: 110, align: 'right', sorter: (a, b) => (a.onhand_qty ?? 0) - (b.onhand_qty ?? 0) },
    { title: 'Transfer Qty', key: 'transfer_qty', width: 160, render: (_, row) => {
        const current = qtyEdits[row.key] ?? 0;
        return (
          <InputNumber
            min={0}
            max={row.onhand_qty}
            value={current}
            onChange={(v) => setQtyEdits(prev => ({ ...prev, [row.key]: typeof v === 'number' ? v : 0 }))}
            style={{ width: 140 }}
            disabled={!canTransact}
          />
        );
      }
    },
  ], [qtyEdits, whMap]);

  const selectedCount = useMemo(() => selectedKeys.length, [selectedKeys]);

  const handleSubmit = async () => {
    try {
      if (!fromWarehouseId) { message.warning('Please select source warehouse'); return; }
      if (!canTransact) { message.error('You are not an owner of the source warehouse. Transfer is restricted.'); return; }
      if (!toWarehouseId) { message.warning('Please select destination warehouse'); return; }
      if (fromWarehouseId === toWarehouseId) { message.warning('Source and destination must be different'); return; }
      if (selectedKeys.length === 0) { message.warning('Please select at least one material'); return; }

      // Build payloads only for rows with qty > 0
      const rowsByKey: Record<string, InventoryRow> = Object.fromEntries(filtered.map(r => [r.key, r]));
      const payloads = selectedKeys
        .map(k => String(k))
        .map(k => ({ row: rowsByKey[k], qty: Number(qtyEdits[k] || 0) }))
        .filter(x => x.row && x.qty > 0 && x.qty <= (x.row.onhand_qty || 0))
        .map(x => ({
          op_type: 'transfer',
          mat_id: x.row.mat_id,
          qty: x.qty,
          unit: x.row.unit,
          from_warehouse_id: fromWarehouseId,
          to_warehouse_id: toWarehouseId,
          txn_date: date || undefined,
          reference_no: `XFER#${x.row.mat_id}-${fromWarehouseId}->${toWarehouseId}`,
          notes: x.row.description || undefined,
        }));

      if (payloads.length === 0) { message.warning('Please enter valid quantities for selected rows'); return; }

      setSubmitting(true);
      await insertData('mat_gap_txns', payloads);
      if (typeof refetchInventory === 'function') {
        await refetchInventory();
      }
      message.success(`Created ${payloads.length} transfer transaction(s)`);
      setSelectedKeys([]);
      setQtyEdits({});
    } catch (err) {
      console.error(err);
      message.error('Failed to create transfer transactions');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Transfer Between Warehouses</Title>
              <Text type="secondary">Create mat_gap_txns(op_type='transfer') with editable quantities</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Select
              placeholder="From Warehouse"
              allowClear
              value={fromWarehouseId}
              onChange={(v) => { setFromWarehouseId(v); setSelectedKeys([]); setQtyEdits({}); }}
              options={fromWhOptions}
              style={{ width: 220 }}
            />
            <Select
              placeholder="To Warehouse"
              allowClear
              value={toWarehouseId}
              onChange={(v) => setToWarehouseId(v)}
              options={toWhOptions}
              style={{ width: 220 }}
            />
            <Select
              placeholder="Group"
              allowClear
              showSearch
              value={groupFilter || undefined}
              onChange={(v) => setGroupFilter(v || '')}
              options={groupOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Ext Group"
              allowClear
              showSearch
              value={extGroupFilter || undefined}
              onChange={(v) => setExtGroupFilter(v || '')}
              options={extGroupOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 200 }}
            />
            <DatePicker
              allowClear
              value={date ? dayjs(date) : null}
              onChange={(d) => setDate(d ? d.format('YYYY-MM-DD') : null)}
              disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
            />
            <span>
              <Text strong>Show Zero On-hand</Text>{' '}
              <Switch checked={showZeroOnhand} onChange={setShowZeroOnhand} />
            </span>
            <Input
              placeholder="Search material/description"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
            />
            <Button action="add" type="primary" onClick={handleSubmit} loading={submitting} disabled={!hasAnyOwned}>
              {`Create Transfer (${selectedCount || 0})`}
            </Button>
            <Button onClick={() => { setFromWarehouseId(undefined); setToWarehouseId(undefined); setDate(dayjs().format('YYYY-MM-DD')); setSearch(''); setGroupFilter(''); setExtGroupFilter(''); setSelectedKeys([]); setQtyEdits({}); }}>
              Reset
            </Button>
          </Space>
        </Col>
      </Row>

      {fromWarehouseId ? (
        <div style={{ marginBottom: 8 }}>
          <Alert
            type={canTransact ? 'info' : 'warning'}
            showIcon
            message={canTransact
              ? 'Prepare transfer from the selected source warehouse.'
              : 'You are not an owner of the selected source warehouse. Transfer is restricted.'}
          />
        </div>
      ) : null}

      <StandardTable<InventoryRow>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => r.key}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys, rows) => {
            const newSet = new Set((keys || []).map(k => String(k)));
            const oldSet = new Set((selectedKeys || []).map(k => String(k)));
            const nextEdits: Record<string, number> = { ...qtyEdits };

            // Prefill qty for newly selected rows with full on-hand
            (rows as InventoryRow[]).forEach(r => {
              const k = String(r.key);
              if (!oldSet.has(k)) {
                nextEdits[k] = Number(r.onhand_qty) || 0;
              }
            });

            // Clear qty for deselected rows
            (selectedKeys || []).forEach(prevKey => {
              const k = String(prevKey);
              if (!newSet.has(k)) {
                delete nextEdits[k];
              }
            });

            setSelectedKeys(keys);
            setQtyEdits(nextEdits);
          },
          preserveSelectedRowKeys: true,
          getCheckboxProps: (record) => ({ disabled: !fromWarehouseId || !canTransact || record.warehouse_id !== fromWarehouseId || (record.onhand_qty || 0) <= 0 }),
        }}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}


